// components/Common/BiometricModal.jsx (Bootstrap version)
import React, { useEffect } from "react";
import {
  Modal,
  Button,
  Spinner,
  ProgressBar,
  Alert,
  Card,
  Badge,
  Row,
  Col,
} from "react-bootstrap";
import {
  Fingerprint,
  ExclamationTriangle,
  CheckCircle,
  Power,
  Info,
  ArrowRepeat,
} from "react-bootstrap-icons";
import useBiometricCapture from "../../hooks/useBiometricCapture"; // adjust path

// Custom CSS for pulse animation
const pulseStyle = `
  .pulse-icon {
    position: relative;
    display: inline-flex;
  }
  .pulse-icon::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 50%;
    border: 2px solid #0d6efd;
    animation: pulse-ring 1.5s ease-out infinite;
  }
  @keyframes pulse-ring {
    0% {
      transform: scale(0.95);
      opacity: 0.7;
    }
    70% {
      transform: scale(1.2);
      opacity: 0;
    }
    100% {
      transform: scale(0.95);
      opacity: 0;
    }
  }
`;

const BiometricModal = ({ open, onClose, onSuccess, aadhaarNumber, wadh }) => {
  const {
    biometricStatus,
    activeStep,
    biometricProgress,
    steps,
    checkDevice,
    capture,
    retry,
    reset,
  } = useBiometricCapture();

  // Inject pulse animation CSS
  useEffect(() => {
    const styleTag = document.createElement("style");
    styleTag.textContent = pulseStyle;
    document.head.appendChild(styleTag);
    return () => document.head.removeChild(styleTag);
  }, []);

  // Auto‑check device when modal opens
  useEffect(() => {
    if (open) {
      checkDevice();
    } else {
      reset();
    }
  }, [open, checkDevice, reset]);

  const handleCapture = async () => {
    try {
      const data = await capture(aadhaarNumber, wadh);
      onSuccess(data);
      onClose(); // close after success
    } catch (error) {
      // error already stored in biometricStatus
    }
  };

  // Custom stepper using Bootstrap grid
  const renderStepper = () => (
    <div className="position-relative mb-4">
      <Row className="g-0 justify-content-between">
        {steps.map((label, idx) => (
          <Col
            key={label}
            className="text-center"
            style={{ position: "relative", zIndex: 1 }}
          >
            <div
              className={`rounded-circle ${
                idx <= activeStep ? "bg-primary" : "bg-secondary"
              } bg-opacity-25 d-inline-flex align-items-center justify-content-center`}
              style={{ width: 32, height: 32 }}
            >
              <span
                className={`fw-bold ${
                  idx <= activeStep ? "text-black" : "text-white"
                }`}
              >
                {idx + 1}
              </span>
            </div>
            <div
              className={`small mt-1 ${
                idx <= activeStep ? "text-primary" : "text-secondary"
              }`}
            >
              {label}
            </div>
          </Col>
        ))}
      </Row>
      {/* Connector lines - separate absolute positioned */}
      <div
        className="position-absolute top-0 start-0 w-100"
        style={{ height: 32, zIndex: 0 }}
      >
        {steps.slice(0, -1).map((_, idx) => (
          <div
            key={idx}
            className="position-absolute"
            style={{
              left: `${((idx + 1) / steps.length) * 100}%`,
              top: 16,
              width: `${(1 / steps.length) * 100}%`,
              transform: "translateX(-50%)",
              height: 2,
            }}
          >
            <div
              className={`h-100 ${idx < activeStep ? "bg-primary" : "bg-secondary"}`}
              style={{ width: "100%" }}
            />
          </div>
        ))}
      </div>
    </div>
  );
  const renderContent = () => {
    // Loading state – with fingerprint GIF
    if (biometricStatus.loading) {
      return (
        <div className="text-center py-4">
          <div className="mb-3">
            <img
              src="/assets/images/fingerprint.gif"
              alt="Fingerprint animation"
              style={{ width: 120, height: 120, objectFit: "contain" }}
            />
            <Spinner
              animation="border"
              variant="primary"
              className="d-block mx-auto mt-2"
            />
          </div>
          <h6 className="mb-2">{biometricProgress.message}</h6>
          {biometricProgress.message === "Ready to capture fingerprint..." && (
            <p className="text-muted mb-3">Place your finger on the device</p>
          )}
          <ProgressBar
            now={biometricProgress.percentage}
            striped
            animated
            className="my-3"
          />
          <small className="text-muted">
            Step {biometricProgress.step} of 6
          </small>
        </div>
      );
    }

    // Error state
    if (biometricStatus.error) {
      return (
        <Alert variant="danger" className="text-center py-4">
          <ExclamationTriangle size={40} className="mb-3" />
          <h5 className="mb-2">Biometric Error</h5>
          <p>{biometricStatus.error}</p>
          <div className="d-flex gap-2 justify-content-center">
            <Button variant="outline-danger" onClick={retry}>
              <ArrowRepeat className="me-2" />
              Retry Capture
            </Button>
          </div>
        </Alert>
      );
    }

    // Success state
    if (biometricStatus.captureData) {
      return (
        <Alert variant="success" className="text-center py-4">
          <CheckCircle size={40} className="mb-3" />
          <h5 className="mb-2">Biometric Captured Successfully!</h5>
          <p>Closing modal...</p>
        </Alert>
      );
    }

    // Device ready state
    if (biometricStatus.deviceInfo) {
      return (
        <div className="text-center py-3">
          <div className="pulse-icon mb-4">
            <Fingerprint size={80} className="text-primary" />
          </div>
          <h5 className="mb-2">Ready to Capture</h5>
          <p className="text-muted mb-4">
            Place your finger on the biometric device when ready
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={handleCapture}
            className="px-5"
          >
            <Fingerprint className="me-2" />
            Start Capture
          </Button>

          <Card className="mt-4 text-start">
            <Card.Body className="py-2">
              <Row className="align-items-center">
                <Col>
                  <small className="text-muted d-block">Device:</small>
                  <strong>{biometricStatus.deviceInfo.info}</strong>
                </Col>
                <Col xs="auto">
                  <Badge bg="success">
                    <Power className="me-1" size={12} />
                    Connected
                  </Badge>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      );
    }

    // Initial check – should not be visible for long
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Initialising biometric device...</p>
      </div>
    );
  };

  return (
    <Modal
      show={open}
      onHide={onClose}
      centered
      size="lg"
      backdrop="static"
      className="biometric-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <Fingerprint className="me-2 text-primary" />
          Biometric Authentication
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {renderStepper()}
        {renderContent()}
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <Button variant="link" className="text-decoration-none">
          <Info className="me-1" />
          Troubleshooting Guide
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BiometricModal;
